import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ArrowUp, ArrowDown, Trash2, Sparkles, Users } from 'lucide-react';
import { toast } from 'sonner';
import FeaturedRecForm from './FeaturedRecForm';
import { REC_TYPE_META } from '@/components/RecommendedForYou';

const api = (path, opts = {}) => fetch(`/api/admin/featured-recommendations${path}`, {
  credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts,
});

const PinnedRow = ({ rec, idx, total, onMove, onToggle, onDelete }) => {
  const meta = REC_TYPE_META[rec.item_type] || REC_TYPE_META.link;
  const Icon = meta.icon;
  const audience = [...(rec.sectors || []), ...(rec.backgrounds || [])];
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${rec.active ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50 opacity-70'}`}
      data-testid={`featured-rec-row-${rec.rec_id}`}>
      <div className={`w-9 h-9 rounded-lg ${meta.bg} flex items-center justify-center flex-shrink-0`}><Icon className={`h-4 w-4 ${meta.color}`} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{rec.preview?.title || <span className="text-red-500">Source item removed</span>}</p>
        <p className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
          <span className={`font-semibold ${meta.color}`}>{meta.label}</span> ·
          <Users className="h-3 w-3" /> {audience.length ? audience.join(', ') : 'Everyone'}
        </p>
      </div>
      <Switch checked={rec.active} onCheckedChange={() => onToggle(rec)} data-testid={`featured-rec-active-${rec.rec_id}`} />
      <button type="button" disabled={idx === 0} onClick={() => onMove(idx, -1)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`featured-rec-up-${rec.rec_id}`}><ArrowUp className="h-4 w-4" /></button>
      <button type="button" disabled={idx === total - 1} onClick={() => onMove(idx, 1)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`featured-rec-down-${rec.rec_id}`}><ArrowDown className="h-4 w-4" /></button>
      <button type="button" onClick={() => onDelete(rec)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`featured-rec-delete-${rec.rec_id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

export default function AdminFeaturedRecsManager() {
  const [recs, setRecs] = useState([]);
  const [options, setOptions] = useState({});

  const load = () => api('').then(r => (r.ok ? r.json() : [])).then(setRecs).catch(() => {});
  useEffect(() => {
    load();
    fetch('/api/profile-options', { credentials: 'include' }).then(r => (r.ok ? r.json() : {})).then(setOptions).catch(() => {});
  }, []);

  const move = async (idx, dir) => {
    const next = [...recs];
    [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
    setRecs(next);
    await api('/reorder', { method: 'POST', body: JSON.stringify({ ids: next.map(r => r.rec_id) }) });
  };
  const toggle = async (rec) => {
    const { rec_id, preview, order, created_at, created_by, ...body } = rec;
    const res = await api(`/${rec_id}`, { method: 'PUT', body: JSON.stringify({ ...body, active: !rec.active }) });
    if (res.ok) load(); else toast.error('Could not update');
  };
  const remove = async (rec) => {
    if (!(await window.appConfirm(`Remove "${rec.preview?.title || 'this item'}" from Recommended For You?`))) return;
    await api(`/${rec.rec_id}`, { method: 'DELETE' });
    load();
  };

  return (
    <Card className="p-5 space-y-5" data-testid="featured-recs-manager">
      <div>
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Dashboard "Recommended For You"</h2>
        <p className="text-sm text-slate-500 mt-1">Pinned items show first, in this order, to members who match the audience. Any of the 6 slots still free are filled automatically with events, courses, BriteLabs, news and forum posts matched to each member's interests and sector, refreshed daily. Things they've already joined or enrolled in are hidden.</p>
      </div>
      <FeaturedRecForm options={options} onCreated={load} />
      <div className="space-y-2" data-testid="featured-recs-list">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pinned ({recs.length})</p>
        {recs.length === 0 && <p className="text-xs text-slate-400 italic">Nothing pinned yet. The panel is fully automatic.</p>}
        {recs.map((r, i) => (
          <PinnedRow key={r.rec_id} rec={r} idx={i} total={recs.length} onMove={move} onToggle={toggle} onDelete={remove} />
        ))}
      </div>
    </Card>
  );
}
